import React from 'react';
import { observer, inject } from 'mobx-react';

import './App.css';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { AppRoutes } from './AppRoutes';
import { Header } from './Header';
import { Navigation } from './Navigation';
import { NavigationButtons } from './NavigationButtons';
import { SpinnerModal } from './components/common/spinner-modal';
import { LoadingStore } from './stores/loading-store';
import { TermsModal } from './components/common/terms-modal';
import { FurtherConsiderationsEntryStore } from './stores/further-considerations-entry-store';
import { ErrorBoundary } from './components/common/error-boundary';
import { InactivityWatcher } from './components/common/inactivity-watcher';

// TODO: put this in a separate file
import { library } from '@fortawesome/fontawesome-svg-core';
import { faTint, faTrashAlt, faEye, faUserPlus, faChartBar,
  faExternalLinkAlt, faFileDownload, faTable, faChevronUp, faChevronDown, faChevronRight,
  faUserFriends, faInfoCircle, faUserEdit, faEdit, faThumbsUp, faThumbsDown,
  faBell, faCalendarCheck, faLock, faCheck, faTimes, faLink }
  from '@fortawesome/free-solid-svg-icons';
library.add(faTint, faTrashAlt, faEye, faUserPlus, faChartBar,
  faExternalLinkAlt, faFileDownload, faTable, faChevronUp, faChevronDown, faChevronRight,
  faUserFriends, faInfoCircle, faUserEdit, faEdit, faThumbsUp, faThumbsDown,
  faBell, faCalendarCheck, faLock, faCheck, faTimes,
  // Marks a section name that jumps to its section — see SectionJump in
  // supplements-summary-panel.tsx. NOT an arrow: ↑ ↓ ▲ ▼ mean a marker's
  // direction everywhere else in this app, so an arrow beside a section name
  // reads as "this marker is high" before it reads as "go here". A chain link
  // is directionless and says only "this is a link".
  faLink);

interface AppProps extends RouteComponentProps<any> {
  loadingStore?: LoadingStore;
  furtherConsiderationsEntryStore?: FurtherConsiderationsEntryStore;
}

@inject('loadingStore', 'furtherConsiderationsEntryStore')
@observer
class App extends React.Component<AppProps, {}> {

  render() {
    const { loadingStore, furtherConsiderationsEntryStore, location } = this.props;
    const showPDF = location.pathname.indexOf('pdf') > -1;

    if (showPDF) {
      return (
        <div className="App">
          {/*
            No SpinnerModal here on purpose. showAppIsBusy is a single boolean
            shared by every in-flight load, so with three of them running (the
            profile, the ranges, the further considerations) it blinks off as
            soon as the first one resolves and back on when the next starts.
            Over a report that is still building, that read as a page breaking
            and repairing itself. The PDF view carries its own "Preparing your
            report..." state instead, unbroken from the first paint through to
            the Export to PDF button.
          */}
          <AppRoutes />
          {/*
            The PDF export runs in its own popup window with its own stores, and
            the practitioner reads and prints there for minutes at a time. It
            feeds the shared activity clock so that time keeps the opener signed
            in, but it never enforces and never renders a modal over a report.
          */}
          <InactivityWatcher heartbeatOnly />
        </div>
      );
    }
    return (
      <div className="App">
        <div className="App-sticky-top">
          <Header />
          <Navigation />
        </div>
        {/*
          Outside the ErrorBoundary on purpose. The boundary unmounts its whole
          subtree when any sibling throws, which would silently disable the
          inactivity sign-out exactly when the app is in a bad state.
        */}
        <InactivityWatcher />
        <ErrorBoundary>
          <NavigationButtons />
          <AppRoutes />
          <SpinnerModal show={loadingStore.showAppIsBusy} />
          <TermsModal furtherConsiderationsEntryStore={furtherConsiderationsEntryStore} />
        </ErrorBoundary>
      </div>
    );
  }
}

export default withRouter<AppProps, React.ComponentType<any>>(App);
